import { PublicHeader } from "../../components/Header/PublicHeader";
import { useEffect, useState } from 'react';
import { getPublicSelectionRoster } from '../../api/selectionRoster';
import styles from './SelectionRosterPage.module.css';

const PAGE_SIZE = 24;

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timerId = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timerId);
  }, [value, delayMs]);

  return debounced;
}

export function SelectionRosterPage() {
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const debouncedQuery = useDebouncedValue(query);

  useEffect(() => {
    setOffset(0);
  }, [debouncedQuery]);

  useEffect(() => {
    let isCancelled = false;
    setLoading(true);
    setError('');

    getPublicSelectionRoster({ query: debouncedQuery, limit: PAGE_SIZE, offset })
      .then((response) => {
        if (isCancelled) return;
        setItems(Array.isArray(response?.items) ? response.items : []);
        setTotal(Number.isFinite(response?.total) ? response.total : 0);
      })
      .catch((err) => {
        if (isCancelled) return;
        setItems([]);
        setTotal(0);
        setError(err.message || 'Не удалось загрузить список участников.');
      })
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [debouncedQuery, offset]);

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={styles.page}>
      <PublicHeader />
      <section className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>ЮФУ.Про / Список отбора</p>
          <h1 className={styles.title}>Участники, прошедшие отбор</h1>
          <p className={styles.description}>
            Публичный список участников интенсива. Для публикации выводятся только безопасные
            данные: ФИО, подразделение, специальность, курс и отметка о доступе к проектам со звёздочкой.
          </p>
        </div>
        <div className={styles.summaryCard}>
          <span>Всего в списке</span>
          <strong>{total}</strong>
          <p>Поиск работает по ФИО, подразделению и специальности.</p>
        </div>
      </section>

      <section className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Поиск по ФИО"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}
      {loading ? <div className={styles.empty}>Загружаем список участников...</div> : null}

      {!loading && !items.length ? (
        <div className={styles.empty}>
          {debouncedQuery
            ? 'По этому запросу никто не найден. Попробуй сократить запрос или убрать часть ФИО.'
            : 'Список пока не опубликован.'}
        </div>
      ) : null}

      {!loading && items.length ? (
        <>
          <section className={styles.grid}>
            {items.map((item) => (
              <article key={item.entry_id} className={styles.card}>
                <div className={styles.cardTop}>
                  <span className={styles.statusBadge}>{item.selection_status_label}</span>
                  {item.can_choose_starred ? (
                    <span className={styles.starBadge}>Можно выбирать проекты со звёздочкой</span>
                  ) : null}
                </div>
                <h2>{item.full_name}</h2>
                <dl className={styles.metaList}>
                  <div>
                    <dt>Подразделение</dt>
                    <dd>{item.structural_division || '—'}</dd>
                  </div>
                  <div>
                    <dt>Специальность</dt>
                    <dd>{item.speciality || '—'}</dd>
                  </div>
                  <div>
                    <dt>Курс</dt>
                    <dd>{item.course_label || '—'}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </section>

          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
              disabled={offset === 0}
            >
              Назад
            </button>
            <span>Страница {currentPage} из {totalPages}</span>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
            >
              Далее
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
